"use client";

import Image from "next/image";
import Link from "../../../components/LocaleLink";
import { useLocale } from "../../../components/LocaleProvider";

const PHONE_STANMORE = "02033 016323";

type BulletGroup = {
  title?: string;
  items: string[];
};

type Section = {
  id: string;
  title: string;
  subtitle: string;
  desc: string;
  image: string;
  bullets?: BulletGroup[];
  providerOverrideJsonLd?: Record<string, any>;
};

const sections: Section[] = [
  {
    id: "primary-care",
    title: "Primary Care & Family Medicine",
    subtitle: "First point of contact for everyday health concerns.",
    desc:
      "Continuity of care with experienced clinicians who know you, your medical history and your priorities. We help you navigate prevention, early diagnosis and long-term health planning.",
    image: "/medical/sections/primary-care.jpg",
    bullets: [
      {
        title: "What we help with",
        items: [
          "General consultations for acute and chronic conditions",
          "Cardiovascular risk assessment and blood pressure management",
          "Diabetes, thyroid and metabolic health follow-up",
          "Lifestyle medicine and prevention-focused reviews",
        ],
      },
      {
        title: "For the whole family",
        items: [
          "Paediatric and adolescent consultations",
          "Annual health checks and screening recommendations",
        ],
      },
    ],
  },
  {
    id: "dermatology",
    title: "Dermatology & Skin Health",
    subtitle: "Diagnosis and treatment for medical and cosmetic skin concerns.",
    desc:
      "From acne and rosacea to suspicious moles, rashes and inflammatory skin disease, our dermatology service focuses on accurate diagnosis and effective treatment plans.",
    image: "/medical/sections/dermatology.jpg",
    bullets: [
      {
        title: "Conditions we commonly see",
        items: [
          "Acne and post-acne scarring",
          "Rosacea and facial redness",
          "Eczema, psoriasis and chronic rashes",
          "Hair and scalp conditions",
        ],
      },
      {
        title: "Skin cancer vigilance",
        items: [
          "Mole checks and dermatoscopy",
          "Referral pathways for further imaging or surgery when required",
        ],
      },
    ],
  },
  {
    id: "aesthetic",
    title: "Aesthetic Medicine & Injectables",
    subtitle: "Evidence-based aesthetic treatments delivered by medical clinicians.",
    desc:
      "Subtle, natural results using premium products and medical-grade protocols. We focus on facial harmony, skin quality and patient safety.",
    image: "/medical/sections/aesthetic.jpg",
    bullets: [
      {
        title: "Treatments available",
        items: [
          "Anti-wrinkle injections (botulinum toxin)",
          "Dermal fillers for volume restoration and contouring",
          "Medical-grade skin boosters and biorevitalisation",
          "Combined treatment plans for skin quality and texture",
        ],
      },
    ],
  },
  {
    id: "gynaecology-obstetrics",
    title: "Gynaecology & Obstetrics",
    subtitle: "Women’s health, fertility planning and pregnancy care.",
    desc:
      "Confidential, patient-centred care for women at every life stage — from routine screening and contraception through to fertility counselling and pregnancy support.",
    image: "/medical/sections/gynaecology.jpg",
    bullets: [
      {
        title: "Gynaecology",
        items: [
          "Routine gynaecology consultations and annual check-ups",
          "Contraception advice and prescription (including long-acting methods)",
          "Assessment of menstrual irregularities and pelvic pain",
          "Menopause symptom management and hormone health review",
          "STI screening and sexual health counselling",
        ],
      },
      {
        title: "Fertility & Pre-conception",
        items: [
          "Pre-pregnancy health review and blood tests",
          "Fertility assessment and referral where appropriate",
        ],
      },
      {
        title: "Pregnancy Care (Obstetrics)",
        items: [
          "Early pregnancy assessment and reassurance via partner clinics",
          "Monitoring and referral for high-risk pregnancies when clinically indicated",
        ],
      },
    ],
  },

  // ✅ REMOVED: Physiotherapy & Musculoskeletal Care

  {
    id: "diagnostics",
    title: "Diagnostics & Preventive Screening",
    subtitle: "Laboratory testing and imaging pathways tailored to you.",
    desc:
      "We use diagnostics to answer clinical questions — not to generate unnecessary testing. Every investigation is explained and linked to a clear next step.",
    image: "/medical/sections/diagnostics.jpg",
    bullets: [
      {
        title: "Laboratory testing",
        items: [
          "Comprehensive blood panels and targeted tests",
          "Cardio-metabolic risk assessment (cholesterol, HbA1c, etc.)",
          "Hormonal profiling when clinically appropriate",
        ],
      },
      {
        title: "Imaging & additional investigations",
        items: [
          "Ultrasound, X-ray, MRI and CT via partner imaging centres",
          "Results review appointments with clear explanations and next steps",
        ],
      },
    ],
  },
  {
    id: "urology",
    title: "Urology",
    subtitle: "Male health, urinary symptoms and prostate assessment.",
    desc:
      "Discreet consultations for urinary symptoms, prostate concerns and male reproductive health. We focus on thorough assessment, investigation when needed and clear next steps.",
    image: "/medical/sections/urology.jpg",
    bullets: [
      {
        title: "What we help with",
        items: [
          "Frequent urination, urgency or urinary discomfort",
          "Prostate health assessment and PSA testing as indicated",
          "Kidney and bladder-related concerns",
          "Male sexual health and erectile dysfunction assessment",
        ],
      },
      {
        title: "Investigations & follow-up",
        items: [
          "Targeted blood and urine testing",
          "Referral for ultrasound or further urological imaging when required",
        ],
      },
    ],
  },
  {
    id: "vitamin-drip",
    title: "IV Vitamin Drip Therapy",
    subtitle: "Medically supervised intravenous vitamin & hydration infusions.",
    desc:
      "Carefully formulated IV drips designed to support recovery, hydration and energy under medical supervision. Every infusion is preceded by a clinical assessment to ensure suitability and safety.",
    image: "/medical/sections/vitamin-drip.jpg",
    bullets: [
      {
        title: "Typical goals",
        items: [
          "Hydration support after travel or illness",
          "Short-term support for fatigue and low energy",
          "Immune-system support protocols when appropriate",
        ],
      },
      {
        title: "How we work",
        items: [
          "Pre-treatment medical assessment and consent",
          "Drips tailored to individual needs and medical history",
          "Clear follow-up advice and integration with broader health plan",
        ],
      },
    ],
  },

  {
    id: "sports-medicine",
    title: "Sports Medicine",
    subtitle: "Sports-related injuries, joint and tendon pain, with targeted injections.",
    desc:
      "Consultations focused on sports injuries, overuse conditions and musculoskeletal pain, with imaging and ultrasound-guided injections when clinically indicated.",
    image: "/medical/sections/sports-medicine.jpg",
    bullets: [
      {
        title: "Common reasons patients book",
        items: [
          "Sports injuries and training-related pain",
          "Tendon problems (e.g., tennis elbow, Achilles tendinopathy)",
          "Shoulder, knee and ankle pain in active patients",
          "Overuse injuries and return-to-activity planning",
        ],
      },
      {
        title: "Treatments & interventions",
        items: [
          "Clinical assessment and rehabilitation planning",
          "Ultrasound-guided injections (PRP, hyaluronic acid, corticosteroids)",
          "Performance medicine principles and injury prevention strategies",
        ],
      },
    ],
    providerOverrideJsonLd: {
      "@type": "Physician",
      name: "Dr. Dan Laurentiu Oproiu",
      description:
        "Senior clinician in sports medicine with extensive experience in elite sport and musculoskeletal care. Former team doctor for the Romanian National Football Team and Steaua București.",
      medicalSpecialty: ["SportsMedicine"],
    },
  },
];

type SectionRo = { title: string; subtitle: string; desc: string; bullets?: BulletGroup[] };

const sectionsRo: Record<string, SectionRo> = {
  "primary-care": {
    title: "Medicină primară și medicină de familie",
    subtitle: "Primul punct de contact pentru problemele de sănătate de zi cu zi.",
    desc: "Continuitate în îngrijire alături de clinicieni cu experiență, care vă cunosc pe dumneavoastră, istoricul medical și prioritățile. Vă ajutăm în prevenție, diagnostic precoce și planificarea sănătății pe termen lung.",
    bullets: [
      {
        title: "Cu ce vă putem ajuta",
        items: [
          "Consultații generale pentru afecțiuni acute și cronice",
          "Evaluarea riscului cardiovascular și managementul tensiunii arteriale",
          "Monitorizarea diabetului, a tiroidei și a sănătății metabolice",
          "Medicină a stilului de viață și evaluări axate pe prevenție",
        ],
      },
      {
        title: "Pentru întreaga familie",
        items: [
          "Consultații pediatrice și pentru adolescenți",
          "Controale anuale de sănătate și recomandări de screening",
        ],
      },
    ],
  },
  dermatology: {
    title: "Dermatologie și sănătatea pielii",
    subtitle: "Diagnostic și tratament pentru probleme ale pielii, medicale și estetice.",
    desc: "De la acnee și rozacee până la alunițe suspecte, erupții cutanate și boli inflamatorii ale pielii, serviciul nostru de dermatologie se concentrează pe un diagnostic precis și pe planuri de tratament eficiente.",
    bullets: [
      {
        title: "Afecțiuni pe care le întâlnim frecvent",
        items: [
          "Acnee și cicatrici post-acneice",
          "Rozacee și roșeață facială",
          "Eczemă, psoriazis și erupții cronice",
          "Afecțiuni ale părului și scalpului",
        ],
      },
      {
        title: "Vigilență față de cancerul de piele",
        items: [
          "Controlul alunițelor și dermatoscopie",
          "Trimitere către investigații imagistice sau chirurgie, atunci când este necesar",
        ],
      },
    ],
  },
  aesthetic: {
    title: "Medicină estetică și tratamente injectabile",
    subtitle: "Tratamente estetice bazate pe dovezi, efectuate de clinicieni medicali.",
    desc: "Rezultate subtile, naturale, cu produse premium și protocoale de nivel medical. Ne concentrăm pe armonia feței, calitatea pielii și siguranța pacientului.",
    bullets: [
      {
        title: "Tratamente disponibile",
        items: [
          "Injecții anti-riduri (toxină botulinică)",
          "Filere dermice pentru refacerea volumului și conturare",
          "Skin boosters de grad medical și biorevitalizare",
          "Planuri combinate de tratament pentru calitatea și textura pielii",
        ],
      },
    ],
  },
  "gynaecology-obstetrics": {
    title: "Ginecologie și obstetrică",
    subtitle: "Sănătatea femeii, planificarea fertilității și îngrijirea sarcinii.",
    desc: "Îngrijire confidențială, centrată pe pacientă, pentru femei în orice etapă a vieții — de la screening de rutină și contracepție până la consiliere în fertilitate și sprijin pe parcursul sarcinii.",
    bullets: [
      {
        title: "Ginecologie",
        items: [
          "Consultații ginecologice de rutină și controale anuale",
          "Consiliere și prescriere de contracepție (inclusiv metode de lungă durată)",
          "Evaluarea tulburărilor de ciclu menstrual și a durerii pelvine",
          "Managementul simptomelor menopauzei și evaluarea sănătății hormonale",
          "Screening pentru ITS și consiliere în sănătatea sexuală",
        ],
      },
      {
        title: "Fertilitate și pregătirea pentru sarcină",
        items: [
          "Evaluarea sănătății înainte de sarcină și analize de sânge",
          "Evaluarea fertilității și trimitere, atunci când este cazul",
        ],
      },
      {
        title: "Îngrijirea sarcinii (obstetrică)",
        items: [
          "Evaluarea sarcinii în stadiu incipient și informare, prin clinici partenere",
          "Monitorizare și trimitere pentru sarcini cu risc crescut, atunci când este indicat clinic",
        ],
      },
    ],
  },
  diagnostics: {
    title: "Investigații și screening preventiv",
    subtitle: "Analize de laborator și circuite imagistice adaptate dumneavoastră.",
    desc: "Folosim investigațiile pentru a răspunde unor întrebări clinice — nu pentru a genera teste inutile. Fiecare investigație este explicată și legată de un pas următor clar.",
    bullets: [
      {
        title: "Analize de laborator",
        items: [
          "Panouri complete de analize de sânge și teste țintite",
          "Evaluarea riscului cardio-metabolic (colesterol, HbA1c etc.)",
          "Profil hormonal, atunci când este indicat clinic",
        ],
      },
      {
        title: "Imagistică și investigații suplimentare",
        items: [
          "Ecografie, radiografie, RMN și CT prin centre imagistice partenere",
          "Consultații de interpretare a rezultatelor, cu explicații clare și pașii următori",
        ],
      },
    ],
  },
  urology: {
    title: "Urologie",
    subtitle: "Sănătatea bărbatului, simptome urinare și evaluarea prostatei.",
    desc: "Consultații discrete pentru simptome urinare, probleme ale prostatei și sănătatea reproductivă masculină. Ne concentrăm pe o evaluare amănunțită, investigații atunci când sunt necesare și pași următori clari.",
    bullets: [
      {
        title: "Cu ce vă putem ajuta",
        items: [
          "Urinări frecvente, urgență urinară sau disconfort urinar",
          "Evaluarea sănătății prostatei și testul PSA, conform indicațiilor",
          "Probleme legate de rinichi și vezică urinară",
          "Sănătate sexuală masculină și evaluarea disfuncției erectile",
        ],
      },
      {
        title: "Investigații și monitorizare",
        items: [
          "Analize țintite de sânge și urină",
          "Trimitere pentru ecografie sau alte investigații imagistice urologice, atunci când este necesar",
        ],
      },
    ],
  },
  "vitamin-drip": {
    title: "Perfuzii intravenoase cu vitamine",
    subtitle: "Perfuzii intravenoase cu vitamine și hidratare, sub supraveghere medicală.",
    desc: "Perfuzii IV formulate cu atenție, menite să susțină recuperarea, hidratarea și energia, sub supraveghere medicală. Fiecare perfuzie este precedată de o evaluare clinică, pentru a asigura caracterul adecvat și siguranța.",
    bullets: [
      {
        title: "Obiective tipice",
        items: [
          "Susținerea hidratării după călătorii sau boală",
          "Sprijin pe termen scurt pentru oboseală și lipsă de energie",
          "Protocoale de susținere a sistemului imunitar, atunci când este cazul",
        ],
      },
      {
        title: "Cum lucrăm",
        items: [
          "Evaluare medicală și consimțământ înainte de tratament",
          "Perfuzii adaptate nevoilor individuale și istoricului medical",
          "Recomandări clare de urmărire și integrare în planul general de sănătate",
        ],
      },
    ],
  },
  "sports-medicine": {
    title: "Medicină sportivă",
    subtitle: "Leziuni sportive, dureri articulare și tendinoase, cu injecții țintite.",
    desc: "Consultații axate pe leziuni sportive, afecțiuni de suprasolicitare și dureri musculo-scheletale, cu investigații imagistice și injecții ghidate ecografic, atunci când este indicat clinic.",
    bullets: [
      {
        title: "Motive frecvente pentru programare",
        items: [
          "Leziuni sportive și dureri legate de antrenament",
          "Probleme tendinoase (de ex. epicondilita laterală, tendinopatia achiliană)",
          "Dureri de umăr, genunchi și gleznă la pacienți activi",
          "Leziuni de suprasolicitare și planificarea revenirii la activitate",
        ],
      },
      {
        title: "Tratamente și intervenții",
        items: [
          "Evaluare clinică și planificarea recuperării",
          "Injecții ghidate ecografic (PRP, acid hialuronic, corticosteroizi)",
          "Principii de medicină a performanței și strategii de prevenire a leziunilor",
        ],
      },
    ],
  },
};

const copy = {
  en: {
    h1: "Medical Services at EgoDent",
    intro:
      "Integrated medical care, diagnostics and specialist input under one roof in Stanmore. From primary care and dermatology to women’s health (gynaecology & obstetrics), urology, IV vitamin therapy, diagnostics and sports medicine.",
    book: "Book Medical Appointment",
    callFull: "Call Stanmore:",
    sameDay: "Same-day appointments may be available depending on clinician schedule.",
    bookSection: "Book",
    call: "Call Stanmore",
    learnMore: "Learn more",
    footerCall: "For medical appointments call",
    or: "or",
    bookOnline: "book online",
  },
  ro: {
    h1: "Servicii medicale la EgoDent",
    intro:
      "Îngrijire medicală integrată, investigații și consult de specialitate sub același acoperiș, în Stanmore. De la medicină primară și dermatologie la sănătatea femeii (ginecologie și obstetrică), urologie, terapie intravenoasă cu vitamine, investigații și medicină sportivă.",
    book: "Programare medicală",
    callFull: "Sună Stanmore:",
    sameDay: "Programări în aceeași zi pot fi disponibile, în funcție de programul medicilor.",
    bookSection: "Programare:",
    call: "Sună Stanmore",
    learnMore: "Aflați mai mult",
    footerCall: "Pentru programări medicale sunați la",
    or: "sau",
    bookOnline: "programați online",
  },
} as const;

const serviceJsonLd = (s: Section) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: `EgoDent — ${s.title}`,
  serviceType: s.title,
  description: s.desc,
  areaServed: [{ "@type": "City", name: "Stanmore" }],
  provider: s.providerOverrideJsonLd || {
    "@type": "MedicalClinic",
    name: "EgoDent Medical Clinic Stanmore",
    telephone: "+44 2033016323",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Stanmore",
      addressCountry: "UK",
    },
  },
});

const clinicJsonLd = {
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  name: "EgoDent Medical Clinic Stanmore",
  url: "https://www.egodent.co.uk/medical",
  telephone: "+44 2033016323",
  medicalSpecialty: [
    "PrimaryCare",
    "Dermatology",
    "Cardiovascular",
    "Immunization",
    "TravelMedicine",
    "SportsMedicine",
    "Gynecologic",
    "Obstetrics",
    "Urologic",
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Stanmore",
    addressCountry: "UK",
  },
};

export default function MedicalPageClient() {
  const locale = useLocale();
  const t = copy[locale];
  const localized: Section[] = sections.map((s) =>
    locale === "ro" ? { ...s, ...sectionsRo[s.id] } : s
  );

  return (
    <>
      {/* HERO */}
      <section className="container-px mx-auto py-16 text-center relative overflow-hidden bg-black text-white">
        <div className="pointer-events-none absolute -bottom-10 left-1/2 -translate-x-1/2 h-32 w-[70%] rounded-full blur-3xl bg-[#27E8A7]/25" />

        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white">
          {t.h1}
        </h1>
        <div className="mt-3 h-px w-20 mx-auto bg-gradient-to-r from-transparent via-[#27E8A7] to-transparent" />
        <p className="mt-4 text-white/70 text-base md:text-lg max-w-3xl mx-auto">
          {t.intro}
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link href="/book-online" className="btn btn-primary">
            {t.book}
          </Link>
          <a href="tel:+442033016323" className="btn btn-outline">
            {t.callFull} {PHONE_STANMORE}
          </a>
        </div>

        <p className="mt-4 text-xs md:text-sm text-white/55">
          {t.sameDay}
        </p>
      </section>

      {/* SECTIONS */}
      {localized.map((s, i) => (
        <section key={s.id} id={s.id} className="relative bg-black text-white">
          <div className="grid md:grid-cols-2 items-stretch">
            <div className="order-2 md:order-1 flex">
              <div
                className="w-full md:max-w-xl lg:max-w-2xl mx-6 md:ml-12 lg:ml-20 my-10 md:my-14 md:self-center
                           bg-black/70 border border-[#27E8A7]/40 backdrop-blur-sm rounded-2xl p-6 md:p-8
                           shadow-[0_0_20px_rgba(39,232,167,0.15)]"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <h2 className="text-2xl md:text-3xl font-semibold text-white">
                  {s.title}
                </h2>
                <p className="mt-2 text-sm uppercase tracking-[0.16em] text-[#27E8A7]/80">
                  {s.subtitle}
                </p>
                <p className="mt-4 text-white/75 text-sm md:text-base leading-relaxed">
                  {s.desc}
                </p>

                {s.bullets && (
                  <div className="mt-5 space-y-4">
                    {s.bullets.map((b, idx) => (
                      <div key={idx}>
                        {b.title && (
                          <div className="text-xs md:text-sm font-semibold text-white/80 mb-1 uppercase tracking-[0.16em]">
                            {b.title}
                          </div>
                        )}
                        <ul className="list-disc list-outside pl-5 text-sm text-white/80 space-y-1.5">
                          {b.items.map((item, j) => (
                            <li key={j}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    href="/book-online"
                    className="btn btn-primary text-xs md:text-sm"
                  >
                    {t.bookSection} {s.title}
                  </Link>

                  <a
                    href="tel:+442033016323"
                    className="btn btn-outline text-xs md:text-sm"
                  >
                    {t.call}
                  </a>

                  <Link
                    href={`/medical/${s.id}`}
                    className="btn btn-outline text-xs md:text-sm"
                  >
                    {t.learnMore}
                  </Link>
                </div>
              </div>
            </div>

            {/* IMAGE COLUMN — poster-style FULL (no crop), no letterbox box effect */}
            <div className="order-1 md:order-2 bg-black flex items-start md:items-center justify-center px-6 md:px-10 py-10 md:py-14">
              <div className="w-full max-w-[760px]">
                {/* TOP soft fade (very subtle) */}
                <div
                  className="
                    pointer-events-none
                    absolute top-0 left-0 right-0
                    h-8 md:h-10
                    z-20
                    bg-gradient-to-b
                    from-black
                    via-black/40
                    to-transparent
                  "
                />

                {/* BOTTOM soft fade (main fix) */}
                <div
                  className="
                    pointer-events-none
                    absolute bottom-0 left-0 right-0
                    h-20 md:h-24
                    z-20
                    bg-gradient-to-t
                    from-black
                    via-black/55
                    to-transparent
                  "
                />

                <Image
                  src={s.image}
                  alt={s.title}
                  width={1400}
                  height={900}
                  className="
                    w-full h-auto
                    max-h-[520px] md:max-h-[560px] lg:max-h-[620px]
                    object-contain
                    rounded-2xl
                    shadow-[0_0_30px_rgba(0,0,0,0.55)]
                    ring-1 ring-white/10
                  "
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  priority={i === 0}
                />
              </div>
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd(s)) }}
          />
        </section>
      ))}

      <section className="container-px mx-auto pb-16 bg-black text-white">
        <div className="rounded-2xl border border-[#27E8A7]/40 bg-black/80 backdrop-blur-sm px-6 py-5 text-center shadow-[0_0_25px_rgba(39,232,167,0.15)]">
          {/* ✅ ONLY CHANGE BELOW: make it wrap cleanly and force the link to be text (not pill) */}
          <p className="text-sm text-white flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <span>{t.footerCall}</span>

            <span className="text-[#27E8A7]">
              Stanmore:{" "}
              <a href="tel:+442033016323" className="underline underline-offset-4">
                {PHONE_STANMORE}
              </a>
            </span>

            <span>{t.or}</span>

            <Link
  href="/book-online"
  className="
    inline-flex items-center whitespace-nowrap
    px-4 py-1.5
    rounded-full
    bg-[#27E8A7]/15
    text-[#27E8A7]
    text-sm
    font-medium
    hover:bg-[#27E8A7]
    hover:text-black
    transition
  "
>
  {t.bookOnline}
</Link>

            <span>.</span>
          </p>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(clinicJsonLd) }}
      />
    </>
  );
}
